import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { SIZE_GROUPS } from "@/config/pogoConfig";

export default function SizeGuideModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="size-guide-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <button
            data-testid="size-guide-backdrop"
            aria-label="Close size guide"
            onClick={onClose}
            className="absolute inset-0 bg-ink/40"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Size guide"
            className="relative w-full max-w-lg bg-ivory rounded-t-3xl sm:rounded-3xl max-h-[85vh] overflow-y-auto no-scrollbar"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="sticky top-0 bg-ivory px-6 py-5 flex items-center justify-between border-b border-ink/10">
              <h2 className="font-display uppercase text-2xl tracking-wide">
                Size guide.
              </h2>
              <button
                data-testid="size-guide-close"
                onClick={onClose}
                aria-label="Close"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-ink text-ivory transition-transform duration-150 active:scale-90"
              >
                <X className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>
            <div className="px-6 py-6 space-y-5 text-sm text-ink/80 leading-relaxed">
              <p>
                POSSO is listed in European (EU) women's sizing. Available at
                launch:
              </p>
              <ul className="space-y-1 font-medium">
                {SIZE_GROUPS.map((g) => (
                  <li key={g.key}>
                    {g.label}: {g.sizes[0]}–{g.sizes[g.sizes.length - 1]}
                  </li>
                ))}
              </ul>
              <div>
                <p className="font-display uppercase tracking-wide text-ink mb-2">
                  Find your fit
                </p>
                <table
                  data-testid="size-conversion-table"
                  className="w-full text-left text-sm"
                >
                  <thead>
                    <tr className="border-b-2 border-ink/15 font-display uppercase text-xs tracking-wide text-ink/60">
                      <th className="py-1.5 pr-3">EU (POSSO)</th>
                      <th className="py-1.5 pr-3">Foot (cm)</th>
                      <th className="py-1.5 pr-3">US Women's</th>
                      <th className="py-1.5">UK</th>
                    </tr>
                  </thead>
                  <tbody className="font-medium">
                    {[
                      ["35", "21.6 – 22.0", "5", "2.5"],
                      ["36", "22.1 – 22.5", "6", "3.5"],
                      ["37", "22.9 – 23.3", "6.5 – 7", "4"],
                      ["38", "23.7 – 24.1", "7.5 – 8", "5"],
                      ["39", "24.6 – 25.0", "8.5 – 9", "6"],
                      ["40", "25.4 – 25.8", "9.5", "6.5"],
                      ["41", "26.2 – 26.6", "10", "7.5"],
                      ["42", "27.1 – 27.5", "10.5 – 11", "8"],
                    ].map(([eu, cm, us, uk]) => (
                      <tr key={eu} className="border-b border-ink/10">
                        <td className="py-1.5 pr-3 font-bold">{eu}</td>
                        <td className="py-1.5 pr-3">{cm}</td>
                        <td className="py-1.5 pr-3">{us}</td>
                        <td className="py-1.5">{uk}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-xs text-ink/50">
                  Conversions are approximate — if you're between sizes, we'll
                  confirm the right fit with you before anything ships.
                </p>
              </div>
              <p>
                <span className="font-display uppercase tracking-wide text-ink">
                  How to measure your foot:
                </span>{" "}
                stand on a piece of paper in the evening, mark your heel and
                longest toe, then measure between the marks in cm. Match that
                length to the Foot (cm) range above — between sizes? Size up.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
